import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError, apiFetch } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { useReauthStore } from '../lib/reauth';

/**
 * Re-authentication modal.
 *
 * Pops automatically when any `apiFetch` call receives a 401 — see
 * `useReauthStore.requestReauth` in `src/lib/reauth.ts`. While open, the
 * page underneath stays mounted, which is the key to preserving in-flight
 * form data:
 *
 * 1. The Radix `Dialog` overlay does NOT unmount sibling components when
 *    it opens. The pack editor, asset uploader, etc. remain in the DOM
 *    with their React Hook Form state intact.
 * 2. Long-lived forms also write a snapshot of their values into
 *    `useReauthStore.formSnapshots` on every change. If a future flow
 *    forces a redirect (e.g. tab close + reopen), the form can rehydrate
 *    from the snapshot on next mount.
 * 3. Once the user signs back in, `resolveReauth()` wakes every paused
 *    `apiFetch` call and the original request is retried exactly once
 *    with `_skipReauth: true`. The user never sees a refresh and the
 *    typed-in values come back when the retried mutation succeeds.
 *
 * Validates Requirement 20.4.
 */

const ReauthSchema = z.object({
  email: z
    .string()
    .min(1, 'Vui lòng nhập email')
    .email('Email không hợp lệ'),
  password: z.string().min(1, 'Vui lòng nhập mật khẩu'),
});

type ReauthValues = z.infer<typeof ReauthSchema>;

interface SignInResponse {
  userId: string;
  expiresAt: number;
}

export default function ReauthModal(): JSX.Element {
  const isOpen = useReauthStore((s) => s.isOpen);
  const resolveReauth = useReauthStore((s) => s.resolveReauth);
  const rejectReauth = useReauthStore((s) => s.rejectReauth);
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReauthValues>({
    resolver: zodResolver(ReauthSchema),
    defaultValues: { email: '', password: '' },
  });

  const [submitError, setSubmitError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      // Use `silent: true` so the global toast layer doesn't double-report
      // a wrong-credentials error that we already render inline below.
      const res = await apiFetch<SignInResponse>('/admin/v1/auth/sign-in', {
        method: 'POST',
        body: values,
        silent: true,
        _skipReauth: true,
      });
      setAuth(res.userId, res.expiresAt);
      reset();
      resolveReauth();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Per Requirement 20.3, do not distinguish wrong email vs wrong
        // password.
        setSubmitError('Email hoặc mật khẩu không đúng');
        return;
      }
      if (err instanceof ApiError && err.code === 'network_down') {
        setSubmitError('Mất kết nối tới máy chủ. Thử lại.');
        return;
      }
      setSubmitError('Có lỗi xảy ra. Thử lại.');
    }
  });

  const handleCancel = (): void => {
    reset();
    setSubmitError(null);
    rejectReauth(
      new ApiError(401, {
        error: {
          code: 'reauth_cancelled',
          message: 'Phiên đăng nhập đã hết hạn',
        },
      }),
    );
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-slate-200 bg-white p-6 shadow-lg focus:outline-none"
          // Prevent closing the dialog by clicking outside or pressing
          // Escape — the user must either re-authenticate or explicitly
          // cancel via the button. This avoids accidentally throwing away
          // a paused request while the user reaches for their password
          // manager.
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Dialog.Title className="text-lg font-semibold text-slate-900">
            Phiên đăng nhập đã hết hạn
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-slate-500">
            Vui lòng đăng nhập lại để tiếp tục. Dữ liệu đang nhập sẽ được
            giữ nguyên.
          </Dialog.Description>

          <form className="mt-4 space-y-3" onSubmit={onSubmit} noValidate>
            <div>
              <label
                htmlFor="reauth-email"
                className="block text-sm font-medium text-slate-700"
              >
                Email
              </label>
              <input
                id="reauth-email"
                type="email"
                autoComplete="username"
                autoFocus
                disabled={isSubmitting}
                aria-invalid={errors.email ? 'true' : 'false'}
                {...register('email')}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
              />
              {errors.email ? (
                <p className="mt-1 text-xs text-red-600" role="alert">
                  {errors.email.message}
                </p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="reauth-password"
                className="block text-sm font-medium text-slate-700"
              >
                Mật khẩu
              </label>
              <input
                id="reauth-password"
                type="password"
                autoComplete="current-password"
                disabled={isSubmitting}
                aria-invalid={errors.password ? 'true' : 'false'}
                {...register('password')}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
              />
              {errors.password ? (
                <p className="mt-1 text-xs text-red-600" role="alert">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            {submitError ? (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {submitError}
              </div>
            ) : null}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {isSubmitting ? 'Đang đăng nhập…' : 'Đăng nhập'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
